import { MouseEvent, useContext, useState } from 'react';
import clsx from 'clsx';

import { Icon, IconButton, List } from '@atom/mui';
import {
  ArrowDropDownIcon,
  ArrowRightIcon,
  CreateNewFolderIcon,
  EditIcon,
  FolderIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { MediaFolderTree } from '@atom/types/media';

import { canCreateSubfolder } from '../filesTabUtilities';

import MoveFolderTreeContext from './MoveFolderTreeContext';
import MoveFolderTreeRowEditor from './MoveFolderTreeRowEditor';

import classes from './moveFolderTree.module.css';

const { ListItemButton, ListItemText } = List;

const styles = {
  primaryText: { fontWeight: 'normal' },
  expanderIcon: { color: colors.neutral.gray },
  actionButton: {
    padding: '0.25rem',
    width: '2.25rem',
    height: '2.25rem',
  },
  actionIcon: { color: colors.neutral.gray },
};

type MoveFolderTreeItemProps = {
  tree: MediaFolderTree;
  level: number;
  hasChildren: boolean;
  isRoot: boolean;
};

const MoveFolderTreeItem = ({
  tree,
  level,
  hasChildren,
  isRoot,
}: MoveFolderTreeItemProps) => {
  const {
    expandedIds,
    onToggleExpand,
    selectedId,
    onSelect,
    disabledIds,
    movingFolderId,
    isFolderMove,
    editing,
    onStartCreate,
    onStartRename,
  } = useContext(MoveFolderTreeContext);

  const [isHovered, setHovered] = useState(false);

  const isExpanded = expandedIds.has(tree.id);
  const isSelected = selectedId === tree.id;

  const isPending = editing?.kind === 'create' && editing.tempId === tree.id;
  const isRenamingThis = editing?.kind === 'rename' && editing.id === tree.id;
  const isEditing = isPending || isRenamingThis;

  const isDisabled =
    disabledIds.has(tree.id) ||
    isPending ||
    (isFolderMove && (tree.ancestors?.includes(movingFolderId) ?? false));

  const handleToggle = (event: MouseEvent) => {
    event.stopPropagation();
    onToggleExpand(tree.id);
  };

  const handleClick = () => {
    if (!isDisabled) {
      onSelect(tree);
    }
  };

  const leftIcon = (
    <div
      className={hasChildren ? classes.expanderShown : classes.expanderHidden}
      onClick={handleToggle}
    >
      <Icon
        style={styles.expanderIcon}
        icon={isExpanded ? ArrowDropDownIcon : ArrowRightIcon}
      />
    </div>
  );

  const showActions = !editing && isHovered;
  const showFolderCreate = canCreateSubfolder(level - 1);

  const rightActions = showActions && (
    <div className={classes.actionsContainer}>
      {showFolderCreate && (
        <IconButton
          style={styles.actionButton}
          onClick={() => onStartCreate(tree.id)}
        >
          <Icon style={styles.actionIcon} icon={CreateNewFolderIcon} />
        </IconButton>
      )}
      {!isRoot && (
        <IconButton
          style={styles.actionButton}
          onClick={() => onStartRename(tree.id, tree.name)}
        >
          <Icon style={styles.actionIcon} icon={EditIcon} />
        </IconButton>
      )}
    </div>
  );

  const readRow = (
    <div className={classes.row}>
      <div
        className={clsx(
          classes.textContainer,
          isDisabled && classes.textContainerDisabled,
        )}
      >
        <Icon
          className={clsx(
            classes.folderIcon,
            isDisabled && classes.folderIconDisabled,
          )}
          icon={FolderIcon}
        />
        {tree.name}
      </div>
      {rightActions}
    </div>
  );

  return (
    <ListItemButton
      className={clsx(classes.treeRow, isSelected && classes.treeRowSelected)}
      style={{ paddingLeft: `${level}rem` }}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {leftIcon}
      <ListItemText
        primaryTextStyle={styles.primaryText}
        primary={isEditing ? <MoveFolderTreeRowEditor /> : readRow}
      />
    </ListItemButton>
  );
};

export default MoveFolderTreeItem;
